import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Avatar, Box, IconButton, styled } from '@mui/material';
import defaultEvents2 from '../assets/Announcements/events2.png';
import { generateThumbnail } from '../utils/generateThumbnail';
import { PlayArrow } from '@mui/icons-material';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Blurhash } from 'react-blurhash';
import { encodeImageToBlurhash } from './encoder';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

import defaultimg from './default.png';
import { selectDeviceType } from '../features/Auth/authSlice';
import { useSelector } from 'react-redux';

const videoFiletypes = /mp4|mkv|webm|mov|avi|wmv|flv|3gp|mp3|m4a|flac|hevc|heic|mov|quicktime/;
const StyledBox = styled(Box)(({ theme, sx }) => ({
  borderRadius: '4px',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.pxToRem(24),
  color: theme.palette.text.primary,
  ...sx,
}));
const StyledLazyLoadImage = styled(LazyLoadImage)(({ theme, variant, sx }) => ({
  objectFit: 'cover',
  width: theme.spacing(5),
  height: theme.spacing(5),
  borderRadius: variant === 'circle' ? '50%' : '4px',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.pxToRem(24),
  color: theme.palette.text.primary,
  ...sx,
}));

const AttachAvatar = ({
  src,
  size = null,
  controls = true,
  autoPlay = false,
  muted = false,
  sx = {},
  publicCall = true,
  isVideoPreview = false,
  onHandleLightBox = () => {},
  isDetailScreen = false,
  ...props
}) => {
  // console.time('blurhash');
  const [mediaError, setMediaError] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  // const [blurhash, setBlurhash] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  // Memoize the video check to avoid recalculating it on every render
  const isVideo = useMemo(() => videoFiletypes?.test(src?.split('.').pop()), [src]);
  // Generate the source URL only if necessary
  const deviceType = useSelector(selectDeviceType);
  let isIOS = deviceType === 'ios';
  const sourceUrl = useMemo(() => {
    if (!src) return null;
    return isVideoPreview ? src : `${process.env.REACT_APP_PUBLIC_UPLOADS_URL}${src}`;
  }, [src, isVideoPreview]);

  // Callback to generate the thumbnail
  const generatePoster = useCallback(async () => {
    if (isVideoPreview || isVideo) {
      try {
        const generatedThumbnailUrl = await generateThumbnail(sourceUrl, isIOS);
        setThumbnailUrl(generatedThumbnailUrl);
      } catch (error) {
        console.log('Thumbnail generation failed:', error);
        setMediaError(true); // fallback display for broken media
      }
    }
  }, [isVideoPreview, isVideo, sourceUrl]);

  // const generateBlurhash = useCallback(async () => {
  //   if (!isVideo && sourceUrl) {
  //     requestIdleCallback(async () => {
  //       const blurhashString = await encodeImageToBlurhash(sourceUrl);
  //       setBlurhash(blurhashString);
  //     });
  //   }
  // }, [isVideo, sourceUrl]);

  useEffect(() => {
    if (sourceUrl) {
      if (isVideo) {
        generatePoster();
      }
      // else {
      //   generateBlurhash();
      // }
    }
  }, [sourceUrl, isVideo, generatePoster]);

  const handleMediaLoad = () => {
    // console.timeEnd('blurhash');
    setIsLoaded(true);
  };

  if (!src) {
    
    return <Avatar src={defaultimg} sx={{ ...sx }} {...props} />;
  }



  // Optimize the rendering logic
  if (publicCall) {
    if (isVideo) {
      if (!autoPlay) {
        return (
          <Box position="relative" sx={{ ...sx }}>
            <img src={thumbnailUrl} style={{ ...sx }} {...props} />
            <IconButton
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <PlayArrow fontSize="large" />
            </IconButton>
          </Box>
        );
      } else {
        return mediaError ? (
          <img
            src={defaultimg}
            alt="fallback"
            className="attachment-avatar"
            style={{
              minHeight: '40vh',
              height: '40vh',
              objectFit: 'contain',
              width: '100%',
            }}
          />
        ) : (
          <video
            src={sourceUrl}
            style={{ ...sx, height: '40vh', objectFit: 'contain', width: '100%'}}
            controls={controls}
            autoPlay={!autoPlay}
            playsInline={isIOS}
            webkit-playsinline={isIOS ? 'true' : undefined}
            controlsList="nodownload noremoteplayback nocaption"
            muted={muted}
            poster={thumbnailUrl}
            {...props}
            onError={() => setMediaError(true)}
          />
        );
      }
    } else {
      // return <Avatar src={sourceUrl} sx={{ ...sx }} {...props} />;
      return (
        <StyledBox
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: !isLoaded ? 'gray' : 'none',
          }}
        >
          <StyledLazyLoadImage
            src={sourceUrl}
            alt="News"
            effect="blur"
            sx={sx}
            wrapperClassName="avatar-wrapper"
            threshold={1000}
            visibleByDefault={true}
            onLoad={handleMediaLoad}
            {...props}
          />
          {isDetailScreen && (
            <IconButton
              onClick={onHandleLightBox}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: '#EEF5',
              }}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
          )}
        </StyledBox>
      );
    }
  } else {
    return isVideoPreview ? (
      <img src={thumbnailUrl} style={{ ...sx }} {...props} />
    ) : (
      <Avatar src={src} sx={{ ...sx }} {...props} />
    );
  }
};

export default React.memo(AttachAvatar);
